import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TODAY } from "../../data/company";
import {
  addDaysIso,
  availableCount,
  // isUrgent,
  listingName,
  usageLabel,
  addListing,
  setSponsoredUntil,
  publicStatus,
  removeListing,
  unitsOf,
} from "../../lib/company";
import type { PublishStatus } from "../../types/company";
import { assetUrl } from "../../lib/api";
import { confirmSponsorCheckout, deleteListing, setListingStatus } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { cn, eur, longDate } from "../../lib/format";
// import { usePublish } from "../../components/company/usePublish";
import { SponsorModal } from "../../components/company/modals";
import { ActionButton, Modal, PageTitle, StatusBadge } from "../../components/company/ui";
import { useT } from "../../i18n";

const linkButton =
  "flex h-8.5 items-center rounded bg-surface px-3 text-[13px] text-ink no-underline hover:bg-surface-hover";

export default function CompanyListings() {
  const t = useT();
  const L = t.company.listings;
  const S = t.company.sponsor;
  const { state, run, toast } = useCompany();
  // const { publish, modal: publishModal } = usePublish();
  const [urgentFor, setUrgentFor] = useState<string | null>(null);
  const [archiveFor, setArchiveFor] = useState<string | null>(null);
  const [deleteFor, setDeleteFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** PATCH …/status for pause/archive; the API's copy of the listing replaces ours. */
  const changeStatus = async (id: string, status: Exclude<PublishStatus, "moderated">) => {
    setBusy(id);
    try {
      const { listing, units, message } = await setListingStatus(id, status);
      run(addListing, listing, units);
      toast.success(message);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    try {
      toast.success(await deleteListing(id));
      run(removeListing, id);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(null);
    }
  };

  // Offered on every published listing, unless the admin turned sponsoring off
  // const sponsorOff = !!state.pricing && (!state.pricing.sponsor.enabled || state.pricing.sponsor.options.length === 0);

  // Back from Stripe Checkout: ?sponsor=success&session_id=… or ?sponsor=cancelled
  const [searchParams, setSearchParams] = useSearchParams();
  const handled = useRef(false);
  useEffect(() => {
    const result = searchParams.get("sponsor");
    if (!result || handled.current) return;
    handled.current = true;
    const sessionId = searchParams.get("session_id");
    setSearchParams({}, { replace: true });
    if (result === "cancelled" || !sessionId) return void toast.info(S.cancelled);
    confirmSponsorCheckout(sessionId)
      .then(({ status, message, listingId, urgentUntil }) => {
        if (status === "paid" && listingId && urgentUntil) {
          run(setSponsoredUntil, listingId, urgentUntil);
          toast.success(S.paid(longDate(urgentUntil)));
        } else if (status === "pending") toast.info(message);
        else toast.warning(message);
      })
      .catch((err) => toast.error(err));
  }, [searchParams, setSearchParams, run, toast, S]);
  const listings = state.listings.filter((l) => l.publish !== "archived");

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageTitle className="mb-0">{L.title}</PageTitle>
        <span className="text-[13px] text-muted">{usageLabel(state)}</span>
      </div>

      <div className="grid gap-2.5">
        {!state.synced && <p className="m-0 text-sm text-muted">{L.loading}</p>}
        {listings.map((l) => {
          const total = unitsOf(state, l.id).length;
          const pub = publicStatus(state, l);
          // const urgent = isUrgent(l);
          const href = `/company/listings/${l.id}`;
          const photo = l.photoUrls?.[0];
          return (
            <div key={l.id} className="flex flex-wrap items-center gap-4 rounded-card bg-white p-4">
              <Link
                to={href}
                aria-label={listingName(l)}
                className="aspect-16/10 w-24 shrink-0 overflow-hidden rounded bg-linear-to-b from-surface to-[#E2E3E4]"
              >
                {photo && <img src={assetUrl(photo)} alt="" className="h-full w-full object-cover" />}
              </Link>
              <Link to={href} className="min-w-50 flex-1 text-ink no-underline">
                <div className="text-base font-medium">{listingName(l)}</div>
                <div className="mt-0.5 text-[13px] text-muted">
                  {l.version} · {l.city}
                </div>
                <div className="mt-1 text-[13px]">
                  {eur(l.weekly)}
                  {t.common.perWeekShort} · {eur(l.monthly)}
                  {t.common.perMonth}
                </div>
              </Link>
              <div className="min-w-32.5 text-[13px]">
                <b className="font-medium">
                  {availableCount(state, l.id, TODAY, addDaysIso(TODAY, 1))} / {total}
                </b>
                <div className="text-xs text-muted">{L.availableToday}</div>
              </div>
              <div className="flex min-w-37.5 flex-col items-start gap-1">
                <StatusBadge kind="publish" status={l.publish} />
                <span className={cn("text-xs", pub.visible ? "text-tone-green-fg" : "text-faint")}>
                  {pub.text}
                </span>
              </div>
              {/* {urgent && (
                <div className="flex min-w-37.5 flex-col items-start gap-1">
                  <span className="rounded bg-brand px-2 py-1 text-[11px] font-semibold uppercase tracking-[.06em] text-white">
                    {S.badge}
                  </span>
                  <span className="text-xs text-ink-soft">{S.until(longDate(l.urgentUntil!))}</span>
                </div>
              )} */}
              <div className="flex flex-wrap gap-1.5">
                <Link to={`${href}/edit`} className={linkButton}>
                  {t.common.edit}
                </Link>
                {/* {(l.publish === "draft" || l.publish === "paused") && (
                  <ActionButton variant="primary" className="h-8.5 font-normal" onClick={() => publish(l.id)}>
                    {t.common.publish}
                  </ActionButton>
                )}
                {l.publish === "published" && (urgent || !sponsorOff) && (
                  <button
                    type="button"
                    onClick={() => setUrgentFor(l.id)}
                    className="inline-flex h-8.5 cursor-pointer items-center rounded border border-brand bg-white px-3 text-[13px] font-medium text-brand hover:bg-tone-blue-bg"
                  >
                    {urgent ? S.extend : S.sponsor}
                  </button>
                )} 
                {l.publish === "published" && (
                  <ActionButton
                    className="h-8.5 font-normal"
                    disabled={busy === l.id}
                    onClick={() => changeStatus(l.id, "paused")}
                  >
                    {L.pause}
                  </ActionButton>
                )}*/}
                {l.publish !== "moderated" && (
                  <ActionButton
                    variant="quiet"
                    className="h-8.5 font-normal"
                    disabled={busy === l.id}
                    onClick={() => setArchiveFor(l.id)}
                  >
                    {L.archive}
                  </ActionButton>
                )}
                {l.publish === "draft" && (
                  <ActionButton
                    variant="quiet"
                    className="h-8.5 font-normal text-tone-red-fg"
                    disabled={busy === l.id}
                    onClick={() => setDeleteFor(l.id)}
                  >
                    {L.delete}
                  </ActionButton>
                )}
              </div>
            </div>
          );
        })}

        {state.synced && listings.length === 0 && (
          <div className="rounded-card bg-white px-6 py-14 text-center">
            <h3 className="m-0 mb-1.5 text-lg font-medium">{L.emptyTitle}</h3>
            <p className="m-0 mb-4 text-sm text-muted">
              {L.emptyText}
            </p>
            <Link
              to="/company/listings/new"
              className="inline-flex h-10 items-center rounded bg-brand px-4.5 text-sm font-medium text-white no-underline hover:bg-brand-dark"
            >
              {t.company.header.addVehicle}
            </Link>
          </div>
        )}
      </div>

      {urgentFor && <SponsorModal listingId={urgentFor} onClose={() => setUrgentFor(null)} />}
      {archiveFor && (
        <Modal
          title={L.archiveTitle}
          text={L.archiveText}
          okLabel={L.archive}
          okVariant="danger"
          onClose={() => setArchiveFor(null)}
          onOk={() => {
            void changeStatus(archiveFor, "archived");
            setArchiveFor(null);
          }}
        />
      )}
      {deleteFor && (
        <Modal
          title={L.deleteTitle}
          text={L.deleteText}
          okLabel={L.delete}
          okVariant="danger"
          onClose={() => setDeleteFor(null)}
          onOk={() => {
            void remove(deleteFor);
            setDeleteFor(null);
          }}
        />
      )}
      {/* {publishModal} */}
    </>
  );
}
